import React, { useEffect, useState, useRef } from 'react';
import { subscribeToasts, toastVariants } from '../../utils/toast';

const MAX_VISIBLE = 4;

const variantStyles = {
  info: toastVariants.info,
  success: toastVariants.success,
  warning: toastVariants.warning,
  error: toastVariants.error,
};

function getVariantConfig(variant) {
  return variantStyles[variant] || variantStyles.info;
}

const ToastHost = () => {
  const [queue, setQueue] = useState([]);
  const timers = useRef(new Map());
  const stylesInjected = useRef(false);

  useEffect(() => {
    if (stylesInjected.current) return;
    const styleId = 'tp-toast-keyframes';
    if (document.getElementById(styleId)) {
      stylesInjected.current = true;
      return;
    }
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      @keyframes tp-toast-in {
        from { transform: translateY(-6px) scale(0.97); opacity: 0; }
        to { transform: translateY(0) scale(1); opacity: 1; }
      }
    `;
    document.head.appendChild(style);
    stylesInjected.current = true;
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeToasts((toast) => {
      setQueue((prev) => {
        const next = [...prev, toast];
        if (next.length > MAX_VISIBLE) {
          const [removed] = next.splice(0, next.length - MAX_VISIBLE);
          if (removed) {
            const timer = timers.current.get(removed.id);
            if (timer) {
              clearTimeout(timer);
              timers.current.delete(removed.id);
            }
          }
        }
        return next;
      });
    });
    return () => {
      unsubscribe();
      timers.current.forEach((timer) => clearTimeout(timer));
      timers.current.clear();
    };
  }, []);

  useEffect(() => {
    queue.forEach((toast) => {
      if (timers.current.has(toast.id)) return;
      const timer = setTimeout(() => {
        setQueue((prev) => prev.filter((item) => item.id !== toast.id));
        timers.current.delete(toast.id);
      }, toast.duration || 4000);
      timers.current.set(toast.id, timer);
    });
  }, [queue]);

  if (!queue.length) return null;

  return (
    <div className="fixed inset-x-0 top-3 z-[1050] flex flex-col items-center gap-2 px-3 sm:items-end sm:px-6 pointer-events-none">
      {queue.map((toast) => {
        const config = getVariantConfig(toast.variant);
        return (
          <div
            key={toast.id}
            className={`pointer-events-auto w-full max-w-sm rounded-2xl border shadow-lg px-4 py-3 sm:px-5 sm:py-4 transition transform ${config.className}`}
            style={{ animation: 'tp-toast-in 160ms ease-out' }}
          >
            <div className="flex items-start gap-3">
              <div className={`text-xl leading-none ${config.accent}`}>
                {config.icon}
              </div>
              <div className="flex-1 min-w-0">
                {toast.title ? (
                  <div className={`text-xs font-semibold uppercase tracking-wide mb-1 ${config.accent}`}>
                    {toast.title}
                  </div>
                ) : null}
                <div className="text-sm leading-snug break-words">
                  {toast.message}
                </div>
              </div>
              <button
                type="button"
                className="ml-2 text-xs font-semibold text-slate-400 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-200 rounded-full px-2 py-1"
                onClick={() => {
                  setQueue((prev) => prev.filter((item) => item.id !== toast.id));
                  const timer = timers.current.get(toast.id);
                  if (timer) {
                    clearTimeout(timer);
                    timers.current.delete(toast.id);
                  }
                }}
              >
                ✕
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ToastHost;


